const STORAGE_PREFIX = "teletrace:activeEhrImportRun:";
const VALIDATION_BANNER_PREFIX = "teletrace:validationCompleteBanner:";
const COMPLETION_HANDLED_PREFIX = "teletrace:handledImportCompletion:";

export const IMPORT_STAGE_LABELS = {
	reading_csv: "Reading CSV",
	importing_ehr_records: "Importing EHR records",
	resolving_client_mappings: "Resolving client mappings",
	resolving_provider_mappings: "Resolving provider mappings",
	matching_teletrace_visits: "Matching TeleTrace visits",
	comparing_time_and_duration: "Comparing time and duration",
	assigning_validation_statuses: "Assigning validation statuses",
	preparing_results: "Preparing results",
};

export const IMPORT_PROGRESS_STAGES = [
	{ key: "reading_csv", label: "Reading CSV" },
	{ key: "importing_ehr_records", label: "Importing EHR records" },
	{ key: "resolving_client_mappings", label: "Resolving client mappings" },
	{ key: "resolving_provider_mappings", label: "Resolving provider mappings" },
	{ key: "matching_teletrace_visits", label: "Matching TeleTrace visits" },
	{ key: "comparing_time_and_duration", label: "Comparing time and duration" },
	{ key: "assigning_validation_statuses", label: "Assigning validation statuses" },
	{ key: "preparing_results", label: "Preparing results" },
];

export const IMPORT_STAGE_SUPPORT_TEXT = {
	reading_csv: "Reading and parsing your CSV file.",
	importing_ehr_records: "Creating the import batch for valid records.",
	resolving_client_mappings: "Matching EHR client identifiers to TeleTrace clients.",
	resolving_provider_mappings: "Matching EHR provider identifiers to TeleTrace staff.",
	matching_teletrace_visits: "Comparing patient dates, times, and visit codes.",
	comparing_time_and_duration: "Comparing visit start, end, and duration values.",
	assigning_validation_statuses: "Assigning validation statuses to each record.",
	preparing_results: "Finalizing your import results.",
};

export const saveActiveImportRun = (organizationId, runId) => {
	if (!organizationId || !runId) return;
	localStorage.setItem(`${STORAGE_PREFIX}${organizationId}`, JSON.stringify({ runId, savedAt: Date.now() }));
};

export const loadActiveImportRun = (organizationId) => {
	if (!organizationId) return null;
	try {
		const raw = localStorage.getItem(`${STORAGE_PREFIX}${organizationId}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed?.runId ? parsed : null;
	} catch {
		return null;
	}
};

export const clearActiveImportRun = (organizationId) => {
	if (!organizationId) return;
	localStorage.removeItem(`${STORAGE_PREFIX}${organizationId}`);
};

export const getImportStageSupportText = (stageKey) =>
	IMPORT_STAGE_SUPPORT_TEXT[stageKey] || "Processing your EHR import.";

export const isImportRunActive = (status) => status === "pending" || status === "processing";

export const isImportRunFinished = (status) => status === "completed" || status === "failed";

export const notifyImportComplete = (fileName) => {
	if (typeof window === "undefined" || !("Notification" in window)) return;
	if (Notification.permission !== "granted") return;

	const label = fileName ? `"${fileName}"` : "Your EHR import";
	new Notification("TeleTrace", {
		body: `${label} has finished processing.`,
	});
};

export const requestImportNotificationPermission = async () => {
	if (typeof window === "undefined" || !("Notification" in window)) return;
	if (Notification.permission === "default") {
		await Notification.requestPermission();
	}
};

export const savePendingValidationBanner = (organizationId, runId) => {
	if (!organizationId || !runId || typeof window === "undefined") return;
	sessionStorage.setItem(
		`${VALIDATION_BANNER_PREFIX}${organizationId}`,
		JSON.stringify({ dismissed: false, runId }),
	);
};

export const loadPendingValidationBanner = (organizationId) => {
	if (!organizationId || typeof window === "undefined") return null;

	try {
		const raw = sessionStorage.getItem(`${VALIDATION_BANNER_PREFIX}${organizationId}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed?.runId || parsed.dismissed) return null;
		return parsed;
	} catch {
		return null;
	}
};

export const dismissPendingValidationBanner = (organizationId) => {
	if (!organizationId || typeof window === "undefined") return;

	try {
		const raw = sessionStorage.getItem(`${VALIDATION_BANNER_PREFIX}${organizationId}`);
		if (!raw) return;
		const parsed = JSON.parse(raw);
		if (!parsed?.runId) return;
		sessionStorage.setItem(
			`${VALIDATION_BANNER_PREFIX}${organizationId}`,
			JSON.stringify({ ...parsed, dismissed: true }),
		);
	} catch {
		// Ignore invalid session storage payloads.
	}
};

export const clearPendingValidationBanner = (organizationId) => {
	if (!organizationId || typeof window === "undefined") return;
	sessionStorage.removeItem(`${VALIDATION_BANNER_PREFIX}${organizationId}`);
};

export const markImportCompletionHandled = (organizationId, runId) => {
	if (!organizationId || !runId || typeof window === "undefined") return;
	sessionStorage.setItem(`${COMPLETION_HANDLED_PREFIX}${organizationId}`, String(runId));
};

export const wasImportCompletionHandled = (organizationId, runId) => {
	if (!organizationId || !runId || typeof window === "undefined") return false;
	return sessionStorage.getItem(`${COMPLETION_HANDLED_PREFIX}${organizationId}`) === String(runId);
};
